import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const contactInfo = [
  {
    icon: 'ri-map-pin-line',
    label: 'Address',
    value: 'P.O. Box 980190, Kajjansi, Uganda',
    link: null,
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'hello@elhaiministries.org',
    link: 'mailto:hello@elhaiministries.org',
  },
  {
    icon: 'ri-time-line',
    label: 'Office Hours',
    value: 'Mon – Fri: 9:00 AM – 5:00 PM (EAT)',
    link: null,
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError('Message cannot exceed 500 characters.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => params.append(k, v));
      await fetch('https://readdy.ai/api/form/d6uorlmdp0k8rarn7o5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[45vh] min-h-[320px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dq9rrrsgx/image/upload/v1774853702/top-contact2_b5yekz.jpg"
            alt="Contact"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/75" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <nav className="text-sm mb-4">
            <Link to="/" className="text-[#ADD8E6] hover:underline cursor-pointer">Home</Link>
            <span className="text-white/50 mx-2">&rsaquo;</span>
            <span className="text-white">Contact</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">Get In Touch</h1>
          <p className="text-[#ADD8E6] mt-3 text-lg">We would love to hear from you</p>
        </div>
      </section>

      <main>
        {/* Contact Section */}
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-2">
                <span className="text-[#ADD8E6] text-xs font-bold uppercase tracking-widest mb-3 block">Reach Out</span>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#003366] mb-6">We Are Here to Help</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  Whether you want to learn more about our programs, explore partnership opportunities, or simply say hello — we are always happy to connect with you.
                </p>
                <div className="space-y-6">
                  {contactInfo.map((item) => (
                    <div key={item.label} className="flex gap-4">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-[#ADD8E6]/20 rounded-xl">
                        <i className={`${item.icon} text-xl text-[#003366]`} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#003366] uppercase tracking-wide mb-1">{item.label}</p>
                        {item.link ? (
                          <a href={item.link} className="text-sm text-[#ADD8E6] hover:underline cursor-pointer">
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-100">
                  <p className="text-xs font-bold text-[#003366] uppercase tracking-wide mb-4">Follow Us</p>
                  <div className="flex gap-3">
                    {['facebook', 'twitter', 'instagram', 'youtube'].map((s) => (
                      <a
                        key={s}
                        href="#"
                        rel="nofollow"
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8F8F8] hover:bg-[#ADD8E6] hover:text-[#003366] text-gray-500 transition-colors cursor-pointer"
                        aria-label={s}
                      >
                        <i className={`ri-${s}-fill text-base`} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3">
                <div className="bg-[#F8F8F8] rounded-2xl p-8 lg:p-10">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#ADD8E6]/30 rounded-full">
                        <i className="ri-check-line text-3xl text-[#003366]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#003366] mb-2">Message Sent!</h3>
                      <p className="text-gray-500 text-sm">
                        Thank you for reaching out. We will get back to you within 48 hours.
                      </p>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                      <h3 className="text-xl font-bold text-[#003366] mb-6">Send Us a Message</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-bold text-[#003366] uppercase tracking-wide mb-2">
                            Full Name <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#ADD8E6]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-[#003366] uppercase tracking-wide mb-2">
                            Email Address <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="your@email.com"
                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#ADD8E6]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#003366] uppercase tracking-wide mb-2">
                          Subject <span className="text-red-400">*</span>
                        </label>
                        <select
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          required
                          className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#ADD8E6] cursor-pointer"
                        >
                          <option value="">Select a subject</option>
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Donation">Donation</option>
                          <option value="Partnership">Partnership Opportunity</option>
                          <option value="Volunteer">Volunteering</option>
                          <option value="Programs">Programs Information</option>
                          <option value="Media">Media Inquiry</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#003366] uppercase tracking-wide mb-2">
                          Message <span className="text-red-400">*</span>
                        </label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          rows={5}
                          placeholder="Tell us how we can help you..."
                          className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#ADD8E6] resize-none"
                        />
                        <p className={`text-xs mt-1 text-right ${form.message.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                          {form.message.length}/500
                        </p>
                      </div>
                      {error && <p className="text-red-500 text-xs">{error}</p>}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3.5 bg-[#003366] text-white font-bold text-sm rounded-lg hover:bg-[#002244] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-[#F8F8F8]">
          <div className="w-full h-80">
            <iframe
              title="El Hai Pentecostal Ministries Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.785370809994!2d32.549776009386235!3d0.23285523273701497!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177d9793ded29b33%3A0x1b3a2f39407e8bb5!2sDoctors&#39;%20Hospital%20Sseguku!5e0!3m2!1sen!2sug!4v1782150948836!5m2!1sen!2sug"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            
            
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#003366] py-16 text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Make a Difference?</h2>
            <p className="text-white/70 mb-8">Support our programs or partner with us to create lasting impact in Uganda.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/donate" className="px-8 py-3 bg-[#ADD8E6] text-[#003366] font-bold rounded-lg hover:bg-white transition-colors cursor-pointer whitespace-nowrap">
                Donate Now
              </Link>
              <a href="mailto:hello@elhaiministries.org" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#003366] transition-colors cursor-pointer whitespace-nowrap">
                Email Us Directly
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
